import { useCallback, useMemo, useState } from "react";

const MY_EXCHANGES_ONLY_PREFIX = "playerai.myExchangesOnly";

/** One stored choice per signed-in person in one tenant. */
export function myExchangesOnlyKey(tenantId: string, userId: string): string {
  return `${MY_EXCHANGES_ONLY_PREFIX}:${tenantId}:${userId}`;
}

function storedPreference(key: string | null): boolean {
  if (key === null) return false;
  try {
    return window.localStorage.getItem(key) === "true";
  } catch {
    return false;
  }
}

/**
 * Task 17 — "My exchanges only", a personal DISPLAY preference for group
 * conversations, off by default and kept in this browser (a preference, never
 * security state). It changes what this reader sees and nothing else: stored
 * messages, Player AI context, triggering, pending requests and every other
 * participant's view are untouched.
 *
 * The choice belongs to the signed-in person in their tenant, so a shared
 * browser never hands one person's view to the next: a different identity
 * reads its own stored choice (off until it sets one). Without an identity
 * there is nothing to keep and the filter stays off; where storage is
 * unavailable, the choice holds for this session only.
 */
export function useMyExchangesOnly(viewer: {
  readonly tenantId: string | null;
  readonly userId: string | null;
}): readonly [boolean, (enabled: boolean) => void] {
  const key =
    viewer.tenantId && viewer.userId
      ? myExchangesOnlyKey(viewer.tenantId, viewer.userId)
      : null;
  const stored = useMemo(() => storedPreference(key), [key]);
  // This session's choice, remembered WITH the identity it was made under, so
  // it never carries over to a different person.
  const [chosen, setChosen] = useState<{
    readonly key: string;
    readonly enabled: boolean;
  } | null>(null);
  const update = useCallback(
    (next: boolean): void => {
      if (key === null) return;
      setChosen({ key, enabled: next });
      try {
        window.localStorage.setItem(key, String(next));
      } catch {
        // Storage unavailable: the choice holds for this session only.
      }
    },
    [key],
  );
  const enabled =
    key !== null && (chosen?.key === key ? chosen.enabled : stored);
  return [enabled, update] as const;
}
